import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { useState, type ReactElement } from 'react';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import type { ProgressTimelineReviewMonth } from '@repo/shared-types';
import { formatMonthKey } from '../../../lib/sessionTime';
import type { ReviewTarget } from './ProgressTab';

/** How many rows the card shows before "Xem tất cả". Six is two school terms
 *  of a single-subject student, which is as far back as a nudge is a nudge. */
// Desktop shows six rows before "Xem tất cả"; phone shows three so the
// card above the timeline stays short (Johnny, 2026-09-04: the card is
// wanted on phone too, it was desktop-only in cycle 3).
const VISIBLE_DESKTOP = 6;
const VISIBLE_PHONE = 3;

/**
 * The "Nhận xét tháng" status card — the second block of the Tiến độ tab's
 * aside, under the Sổ liên lạc block, on every width (three rows on phone, port
 * guide §4.6): on phone the month header's own "Viết nhận xét tháng M" link
 * already covers this, and the aside moves to the top of the tab instead of
 * staying a sidebar.
 *
 * THI-192 §12.4: rows come from the API's `reviewMonths`, NOT from the loaded
 * timeline window. The card used to derive them from `months`, so it listed
 * only the 3 loaded months and disappeared for a student with no review at all
 * — hiding a nudge list exactly when there is most to nudge about. It now hides
 * on one condition only: the student has not been taught a single month yet.
 */
export function ReviewStatusCard({
  reviewMonths,
  onWrite,
  pending = false,
}: {
  reviewMonths: ProgressTimelineReviewMonth[];
  onWrite: (target: ReviewTarget) => void;
  /** The timeline's first page is in flight: show the card's shape, never
   *  hide it (hiding reads as "no months" for the whole load). */
  pending?: boolean;
}): ReactElement | null {
  const { t } = useTranslation('progress');
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();

  if (pending && reviewMonths.length === 0) {
    return (
      <div className="rounded-lg border border-surface-200 bg-surface-0 px-5 py-4" aria-busy="true">
        <Skeleton className="h-3.5 w-28" />
        <div className="mt-3 space-y-2.5">
          <Skeleton className="h-4 w-44" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-44" />
        </div>
      </div>
    );
  }
  if (reviewMonths.length === 0) return null;

  const visibleCount = isMobile ? VISIBLE_PHONE : VISIBLE_DESKTOP;
  const rows = expanded ? reviewMonths : reviewMonths.slice(0, visibleCount);

  return (
    <div className="rounded-lg border border-surface-200 bg-surface-0 px-5 py-4">
      <span className="text-[12.5px] font-semibold text-surface-600">{t('progress.asideReviews')}</span>
      <div className="mt-2.5 flex flex-col gap-2 text-[13px]">
        {rows.map((r) => (
          <div key={`${r.month}-${r.subjectId}`} className="flex items-center gap-2.5">
            {r.hasReview ? (
              <Check className="h-[13px] w-[13px] shrink-0 text-brand-500" strokeWidth={2.5} />
            ) : (
              <span className="mx-[2.5px] h-2.5 w-2.5 shrink-0 rounded-full border-[1.5px] border-surface-300" />
            )}
            <span className={r.hasReview ? 'text-surface-800' : 'text-surface-500'}>
              {t('progress.monthTitle', { month: formatMonthKey(r.month) })} · {r.subjectName}
            </span>
            {!r.hasReview && (
              <button
                type="button"
                onClick={() => onWrite({ month: r.month, subjectId: r.subjectId, subjectName: r.subjectName })}
                className="ml-auto min-h-[44px] text-[12.5px] font-medium text-brand-600 sm:min-h-0"
              >
                {t('progress.asideWrite')}
              </button>
            )}
          </div>
        ))}
        {!expanded && reviewMonths.length > visibleCount && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-0.5 self-start text-[12.5px] font-medium text-brand-600 hover:underline"
          >
            {t('progress.asideShowAll')}
          </button>
        )}
      </div>
    </div>
  );
}
